import { Vital } from '@type/config.type'
import { VitalAttribute, VitalDataMock } from '@type/vital.type'

export const CRITICAL_VITAL_COLOR = '#E53935'
export const ABNORMAL_VITAL_COLOR = '#FFB800'

type VitalValueInput = {
  vital_type: string
  value: string | number
  unit?: string
}

export const getVitalConfig = (vitalTypes: Vital[], vitalType: string) =>
  vitalTypes.find(vital => vital.name === vitalType || vital.key === vitalType)

export const isCriticalVitalValue = (vitalTypes: Vital[], vitalType: string, value: string | number | undefined) => {
  if (value === '' || value === null || value === undefined) return false

  const numericValue = Number(value)
  if (Number.isNaN(numericValue)) return false

  const vitalConfig = getVitalConfig(vitalTypes, vitalType)
  if (!vitalConfig) return false

  return (
    (vitalConfig.critical_low !== null &&
      vitalConfig.critical_low !== undefined &&
      numericValue <= vitalConfig.critical_low) ||
    (vitalConfig.critical_high !== null &&
      vitalConfig.critical_high !== undefined &&
      numericValue >= vitalConfig.critical_high)
  )
}

export const isAbnormalVitalValue = (vitalTypes: Vital[], vitalType: string, value: string | number | undefined) => {
  if (value === '' || value === null || value === undefined) return false

  const numericValue = Number(value)
  if (Number.isNaN(numericValue)) return false

  const vitalConfig = getVitalConfig(vitalTypes, vitalType)
  if (!vitalConfig?.normal_range_adult) return false

  // Outside normal but not critical (critical handled separately for red).
  if (isCriticalVitalValue(vitalTypes, vitalType, value)) return false

  const [low, high] = vitalConfig.normal_range_adult
  return numericValue < low || numericValue > high
}

export const getVitalValueColor = (vitalTypes: Vital[], vitalType: string, value: string | number | undefined) => {
  if (isCriticalVitalValue(vitalTypes, vitalType, value)) return CRITICAL_VITAL_COLOR
  if (isAbnormalVitalValue(vitalTypes, vitalType, value)) return ABNORMAL_VITAL_COLOR
  return undefined
}

/** Aligns with backend VitalSign.compute_priority (low / high / abnormal / normal). */
export const getVitalPriorityLabel = (
  vitalTypes: Vital[],
  vitalType: string,
  value: string | number | undefined
): 'low' | 'high' | 'abnormal' | 'normal' => {
  if (value === '' || value === null || value === undefined) return 'normal'

  const numericValue = Number(value)
  if (Number.isNaN(numericValue)) return 'normal'

  const vitalConfig = getVitalConfig(vitalTypes, vitalType)
  if (!vitalConfig) return 'normal'

  const { critical_low, critical_high, normal_range_adult } = vitalConfig

  if (critical_low !== null && critical_low !== undefined && numericValue <= critical_low) return 'low'
  if (critical_high !== null && critical_high !== undefined && numericValue >= critical_high) return 'high'

  if (normal_range_adult) {
    const [low, high] = normal_range_adult
    if (numericValue < low || numericValue > high) return 'abnormal'
  }

  return 'normal'
}

export const getVitalValueInputClassName = (color: string | undefined) => {
  if (color === CRITICAL_VITAL_COLOR) return 'text-[#E53935]'
  if (color === ABNORMAL_VITAL_COLOR) return 'text-[#FFB800]'
  return ''
}

export const getVitalEntries = (vital: VitalDataMock): VitalAttribute[] => {
  if (Array.isArray(vital.attributes.vitals) && vital.attributes.vitals.length > 0) {
    return vital.attributes.vitals.map(item => ({
      id: item.id,
      unit: item.unit || '',
      value: item.value,
      vital_type: item.vital_type
    }))
  }

  return [
    {
      id: vital.id,
      unit: vital.attributes.unit || '',
      value: vital.attributes.value,
      vital_type: vital.attributes.vital_type
    }
  ]
}

export const getVitalCapturedDateTime = (vital: VitalDataMock): string => {
  const { captured_date, time, superseded_at, created_at, updated_at } = vital.attributes

  if (captured_date && time && time !== '?') {
    return `${captured_date} ${time}`
  }

  return superseded_at || created_at || updated_at || ''
}

export const findVitalEntry = (vital: VitalDataMock, vitalType: string) =>
  getVitalEntries(vital).find(entry => entry.vital_type === vitalType)

/**
 * Resolve the correction chain for a single vital type.
 *
 * Correction linkage: a record carrying `corrects_id` points at the older
 * record it replaced. The active head is the record whose `id` is never
 * referenced by anyone's `corrects_id`. We walk the chain from that head
 * backwards through `corrects_id` and return it **oldest → newest**.
 */
export const getVitalChain = (vitals: VitalDataMock[], vitalType: string): VitalDataMock[] => {
  const matching = vitals.filter(vital => findVitalEntry(vital, vitalType))
  if (matching.length === 0) return []

  const byId = new Map<string, VitalDataMock>(matching.map(vital => [vital.id, vital]))
  const referencedIds = new Set<string>()

  matching.forEach(vital => {
    const { corrects_id } = vital.attributes
    if (corrects_id !== null && corrects_id !== undefined) {
      referencedIds.add(String(corrects_id))
    }
  })

  // Active head: not referenced as anyone's correction target.
  const head =
    matching.find(vital => !referencedIds.has(vital.id)) ||
    // Fallback: the active, non-superseded record; otherwise the most recent.
    matching.find(vital => vital.attributes.active !== false && !vital.attributes.superseded_at) ||
    [...matching].sort(
      (a, b) => new Date(getVitalCapturedDateTime(b)).getTime() - new Date(getVitalCapturedDateTime(a)).getTime()
    )[0]

  // Walk `corrects_id` backwards to the oldest record, then emit oldest → newest.
  const newestFirst: VitalDataMock[] = []
  const visited = new Set<string>()
  let current: VitalDataMock | undefined = head
  while (current && !visited.has(current.id)) {
    visited.add(current.id)
    newestFirst.push(current)
    const previousId = current.attributes.corrects_id
    if (previousId === null || previousId === undefined) break
    current = byId.get(String(previousId))
  }

  return newestFirst.reverse()
}

export const calculateBmi = (heightCm?: string | number, weightKg?: string | number) => {
  const height = Number(heightCm)
  const weight = Number(weightKg)

  if (!height || !weight || Number.isNaN(height) || Number.isNaN(weight)) return ''

  const heightMeters = height / 100
  return (weight / (heightMeters * heightMeters)).toFixed(1)
}

export const isHeightVital = (vitalType: string) => vitalType.toLowerCase() === 'height'
export const isWeightVital = (vitalType: string) => vitalType.toLowerCase() === 'weight'
export const isBmiVital = (vitalType: string) =>
  vitalType.toLowerCase() === 'bmi' || vitalType.toLowerCase().includes('body mass index')

export const getBmiValue = (vitals: VitalValueInput[]) => {
  const height = vitals.find(vital => isHeightVital(vital.vital_type))?.value
  const weight = vitals.find(vital => isWeightVital(vital.vital_type))?.value

  return calculateBmi(height, weight)
}

export const getCorrectedVitalsForType = (vitals: VitalDataMock[], vitalType: string) => {
  // `getVitalChain` returns oldest → newest, so the newest is the current value.
  const chain = getVitalChain(vitals, vitalType)
  const currentVital = chain[chain.length - 1]
  const previousVitals = chain.slice(0, -1)

  return {
    currentVital,
    previousVitals
  }
}

// ---------------------------------------------------------------------------
// State-change-only sparse timeline
//
// A vital sign only appears at a timestamp if its value actually changed from
// the previous timestamp. Same-moment corrections resolve to the newest
// `created_at` (the corrected/active value wins), and consecutive equal values
// collapse. The result is a sparse matrix keyed by timestamp.
// ---------------------------------------------------------------------------

export type VitalHistoryPoint = {
  timestamp: string
  value: string
  unit: string
  record: VitalDataMock
}

const getCreatedMs = (record: VitalDataMock) => new Date(record.attributes.created_at).getTime()

/**
 * Oldest → newest history for one vital type, after:
 *   1. resolving same-moment corrections (newest `created_at` wins), and
 *   2. dropping consecutive equal-value duplicates (state-change only).
 */
export const getVitalHistory = (vitals: VitalDataMock[], vitalType: string): VitalHistoryPoint[] => {
  const matching = vitals.filter(vital => findVitalEntry(vital, vitalType))
  if (matching.length === 0) return []

  // Bucket by capture timestamp; within a bucket the latest `created_at` wins
  // (that is the corrected/active value for that moment).
  const buckets = new Map<string, VitalDataMock>()
  matching.forEach(record => {
    const key = getVitalCapturedDateTime(record)
    if (!key) return
    const existing = buckets.get(key)
    if (!existing || getCreatedMs(record) > getCreatedMs(existing)) {
      buckets.set(key, record)
    }
  })

  const history: VitalHistoryPoint[] = []
  let lastValue: string | undefined

  Array.from(buckets.entries())
    .sort((a, b) => new Date(a[0]).getTime() - new Date(b[0]).getTime())
    .forEach(([timestamp, record]) => {
      const entry = findVitalEntry(record, vitalType)
      if (!entry) return
      // Drop unchanged consecutive values — only state changes enter the timeline.
      if (lastValue !== undefined && entry.value === lastValue) return
      lastValue = entry.value
      history.push({ timestamp, value: entry.value, unit: entry.unit, record })
    })

  return history
}
